import { useContext, useEffect, useState } from "react";
import { StateContext } from "../../../../context/globalContext/context";
import { iGlobalContext } from "../../../../context/globalContext/interface";
import { getBookedFlightAndHotelSummaryAPI } from "../../../../api/tripDetails/getBookedFlightAndHotelSummary";
import { getPostHotelsSummaryAPI } from "../../../../api/booking/postBooking";
import {
  collectWebbedHotelNotes,
  isWebbedSupplier,
  WebbedHotelNotes,
} from "../../../../utils/booking/hotel/webbedHotelNotes";

/**
 * WebBeds' mandatory disclosures (tariff notes + non-included property fees) for
 * the booking just confirmed, for the confirmation page.
 *
 * Same two reads My Trips makes (useTripDetailsPageLogic): `/trips/summary` for the
 * stays' transaction ids + supplier, then `/trips/hotels` for the per-stay detail —
 * the fee (`roomInfo.priceInfo.dueAtCheckIn`) only exists on the latter. The second
 * call is skipped unless the summary actually has a WebBeds stay, so a non-WebBeds
 * booking costs one extra read and renders nothing.
 *
 * Runs once `enabled` (confirmation finished, booking not failed). Best-effort: any
 * failure leaves the list empty and the confirmation page exactly as it was.
 */
export default function useWebbedHotelNotes(itineraryID: string, enabled: boolean): WebbedHotelNotes[] {
  const { accessJWT, userInfo }: iGlobalContext = useContext(StateContext);
  const userId = userInfo?.id;
  const [notes, setNotes] = useState<WebbedHotelNotes[]>([]);

  useEffect(() => {
    if (!enabled || !itineraryID || !accessJWT || !userId) return;
    let cancelled = false;
    (async () => {
      try {
        const summary = await getBookedFlightAndHotelSummaryAPI(accessJWT, { userId, itineraryID });
        const bookings: any[] = summary?.data?.hotel?.bookingInfo || [];
        const webbedTxnIds = bookings
          .filter((booking) => isWebbedSupplier(booking?.supplier))
          .map((booking) => booking?.transactionId)
          .filter(Boolean);
        if (webbedTxnIds.length === 0 || cancelled) return;

        const hotels = await getPostHotelsSummaryAPI(accessJWT, {
          userId,
          itineraryID,
          transactionId: webbedTxnIds,
        });
        if (cancelled || hotels?.statusCode !== 200) return;
        // Only WebBeds stays were requested, so a `/trips/hotels` row that comes
        // back without its own `supplier` is still one of them.
        const detailed: any[] = (hotels?.data?.hotel?.bookingInfo || []).map((booking: any) => ({
          supplier: "WEBBED",
          ...booking,
        }));
        setNotes(collectWebbedHotelNotes(detailed));
      } catch {
        /* the disclosures are additive — never let them break the confirmation page */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, itineraryID, accessJWT, userId]);

  return notes;
}
